import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Plus, Search, Trash2, X, ChevronDown, ChevronRight, Pencil, Printer } from 'lucide-react';
import { toast } from 'sonner';

// Credit Note — recorded when a new invoice replaces or cancels a previously
// raised invoice (sales side). Optionally linked to a sales order.
const blankItem = () => ({ sku: '', name: '', quantity: 0, unit_price: 0, gst_rate: 18 });

const CreditNotes = () => {
  const [notes, setNotes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [editing, setEditing] = useState(null);

  const emptyForm = {
    sales_order_id: '', customer_id: '', original_invoice_number: '', reason: '',
    notes: '', items: [blankItem()]
  };
  const [formData, setFormData] = useState({ ...emptyForm });

  const openCreate = () => { setEditing(null); setFormData({ ...emptyForm, items: [blankItem()] }); setShowModal(true); };
  const openEdit = (cn) => {
    setEditing(cn);
    setFormData({
      sales_order_id: cn.sales_order_id || '', customer_id: cn.customer_id || '',
      original_invoice_number: cn.original_invoice_number || '', reason: cn.reason || '', notes: cn.notes || '',
      items: (cn.items || []).map(it => ({ sku: it.sku, name: it.name, quantity: it.quantity, unit_price: it.unit_price, gst_rate: it.gst_rate }))
    });
    setShowModal(true);
  };

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [nRes, oRes, cRes, iRes] = await Promise.all([
        api.get('/sales/credit-notes'),
        api.get('/sales/orders'),
        api.get('/purchase/customers'),
        api.get('/sales/inventory')
      ]);
      setNotes(nRes.data); setOrders(oRes.data); setCustomers(cRes.data); setInventory(iRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const onOrderChange = (orderId) => {
    const order = orders.find(o => o.id === orderId);
    if (!order) { setFormData(prev => ({ ...prev, sales_order_id: '' })); return; }
    setFormData(prev => ({
      ...prev,
      sales_order_id: orderId,
      customer_id: order.customer_id || prev.customer_id,
      original_invoice_number: order.order_number || prev.original_invoice_number,
      items: (order.items || []).map(it => ({
        sku: it.sku, name: it.name, quantity: it.quantity,
        unit_price: it.unit_price, gst_rate: it.gst_rate
      }))
    }));
  };

  const addItem = () => setFormData(prev => ({ ...prev, items: [...prev.items, blankItem()] }));
  const removeItem = (idx) => setFormData(prev => prev.items.length > 1 ? ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }) : prev);
  const updateItem = (idx, field, val) => {
    setFormData(prev => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: val };
      if (field === 'sku' && val) {
        const inv = inventory.find(i => i.sku === val);
        if (inv) { items[idx].name = inv.name; items[idx].unit_price = inv.unit_price || 0; items[idx].gst_rate = inv.gst_rate || 18; }
      }
      return { ...prev, items };
    });
  };

  const computeItem = (item) => {
    const taxable = (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
    const gst = (taxable * (parseFloat(item.gst_rate) || 0)) / 100;
    return {
      ...item,
      quantity: parseInt(item.quantity) || 0,
      unit_price: parseFloat(item.unit_price) || 0,
      gst_rate: parseFloat(item.gst_rate) || 0,
      taxable_amount: taxable, cgst: gst / 2, sgst: gst / 2, igst: 0, total_amount: taxable + gst
    };
  };

  const formTotal = formData.items.reduce((s, it) => s + computeItem(it).total_amount, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id) { toast.error('Select a customer'); return; }
    try {
      const payload = { ...formData, items: formData.items.map(computeItem) };
      if (editing) {
        await api.put(`/sales/credit-notes/${editing.id}`, payload);
        toast.success('Credit note updated');
      } else {
        await api.post('/sales/credit-notes', payload);
        toast.success('Credit note recorded');
      }
      setShowModal(false); setEditing(null); setFormData({ ...emptyForm }); fetchData();
    } catch (err) {
      const msg = err.response?.status === 403 ? 'You are not authorized to modify credit notes' : (err.response?.data?.detail || 'Failed');
      toast.error(msg);
    }
  };

  const printCN = (cn) => {
    const rows = (cn.items || []).map(it => `
      <tr>
        <td>${it.sku || ''}</td><td>${it.name || ''}</td>
        <td style="text-align:right">${it.quantity ?? ''}</td>
        <td style="text-align:right">${inr(it.unit_price)}</td>
        <td style="text-align:right">${it.gst_rate ?? ''}%</td>
        <td style="text-align:right">${inr(it.total_amount)}</td>
      </tr>`).join('');
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>${cn.credit_note_number}</title>
      <style>
        @page{size:A4;margin:14mm}
        body{font-family:Arial,sans-serif;font-size:12px;color:#111;padding:16px}
        h1{font-size:18px;margin:0 0 4px}
        .meta{display:grid;grid-template-columns:1fr 1fr;gap:4px 24px;margin:12px 0}
        table{width:100%;border-collapse:collapse;margin-top:8px}
        th,td{border:1px solid #666;padding:5px 6px;text-align:left;font-size:11px}
        th{background:#1a237e;color:#fff}
        .totals{margin-top:10px;text-align:right}
        @media print{button{display:none !important}}
      </style></head><body>
      <h1>Credit Note</h1>
      <div class="meta">
        <div><strong>CN No:</strong> ${cn.credit_note_number}</div>
        <div><strong>Date:</strong> ${cn.created_at ? new Date(cn.created_at).toLocaleDateString('en-GB') : '-'}</div>
        <div><strong>Customer:</strong> ${cn.customer_name || '-'}</div>
        <div><strong>GSTIN:</strong> ${cn.customer_gstin || '-'}</div>
        <div><strong>Original Invoice:</strong> ${cn.original_invoice_number || cn.sales_order_number || '-'}</div>
        <div><strong>Reason:</strong> ${cn.reason || '-'}</div>
      </div>
      <table><thead><tr>
        <th>SKU</th><th>Name</th><th>Qty</th><th>Rate</th><th>GST%</th><th>Amount</th>
      </tr></thead><tbody>${rows}</tbody></table>
      <div class="totals">
        <div>Sub Total: ${inr(cn.subtotal)}</div>
        <div>CGST: ${inr(cn.cgst_total)} &nbsp; SGST: ${inr(cn.sgst_total)} &nbsp; IGST: ${inr(cn.igst_total)}</div>
        <div style="font-weight:700;font-size:14px">Total: ${inr(cn.total_amount)}</div>
      </div>
      ${cn.notes ? `<div style="margin-top:10px"><strong>Notes:</strong> ${cn.notes}</div>` : ''}
      <button onclick="window.print()" style="margin-top:20px;padding:8px 24px;cursor:pointer">Print</button>
      </body></html>`);
    w.document.close();
  };

  const deleteNote = async (id, number) => {
    if (!window.confirm(`Delete credit note "${number}"?`)) return;
    try { await api.delete(`/sales/credit-notes/${id}`); fetchData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const filtered = notes.filter(n =>
    (n.credit_note_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (n.customer_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (n.original_invoice_number || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

  return (
    <div data-testid="credit-notes-page">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Credit Notes</h1>
        <button onClick={openCreate} className="btn btn-primary" data-testid="create-cn-btn">
          <Plus size={16} /> Create Credit Note
        </button>
      </div>

      <div className="card mb-4">
        <div className="form-group" style={{ marginBottom: 0, position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
          <input type="text" className="form-input" placeholder="Search by CN #, customer, invoice..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ paddingLeft: '40px' }} data-testid="search-input" />
        </div>
      </div>

      <div className="card">
        {loading ? <div data-testid="loading-indicator">Loading credit notes...</div> : filtered.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)' }}>
            No credit notes found. A credit note is recorded when a new invoice replaces or cancels a previously raised invoice.
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th></th>
                  <th>CN #</th>
                  <th>Customer</th>
                  <th>Original Invoice</th>
                  <th>Items</th>
                  <th className="text-right">Total</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((cn) => (
                  <React.Fragment key={cn.id}>
                    <tr data-testid={`cn-row-${cn.credit_note_number}`} style={{ cursor: 'pointer' }} onClick={() => setExpanded(expanded === cn.id ? null : cn.id)}>
                      <td>{expanded === cn.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                      <td className="font-mono">{cn.credit_note_number}</td>
                      <td>{cn.customer_name}</td>
                      <td className="font-mono text-sm">{cn.original_invoice_number || cn.sales_order_number || '-'}</td>
                      <td>{cn.items?.length || 0} items</td>
                      <td className="text-right tabular-nums font-semibold">{inr(cn.total_amount)}</td>
                      <td className="text-sm">{new Date(cn.created_at).toLocaleDateString('en-GB')}</td>
                      <td>
                        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                          <button onClick={() => openEdit(cn)} className="btn btn-ghost btn-sm" title="Edit"><Pencil size={14} /></button>
                          <button onClick={() => printCN(cn)} className="btn btn-ghost btn-sm" title="Print"><Printer size={14} /></button>
                          <button onClick={() => deleteNote(cn.id, cn.credit_note_number)} className="btn btn-ghost btn-sm" title="Delete"><Trash2 size={14} color="#DC2626" /></button>
                        </div>
                      </td>
                    </tr>
                    {expanded === cn.id && (
                      <tr><td colSpan={8} style={{ background: 'var(--bg-secondary, #f8fafc)', padding: '12px 16px' }}>
                        {cn.reason && <div style={{ fontSize: 12, marginBottom: 6 }}><strong>Reason:</strong> {cn.reason}</div>}
                        <table style={{ width: '100%', fontSize: '12px' }}>
                          <thead><tr><th style={{ padding: 4, textAlign: 'left' }}>SKU</th><th style={{ padding: 4, textAlign: 'left' }}>Name</th><th style={{ padding: 4, textAlign: 'right' }}>Qty</th><th style={{ padding: 4, textAlign: 'right' }}>Rate</th><th style={{ padding: 4, textAlign: 'right' }}>Amount</th></tr></thead>
                          <tbody>
                            {(cn.items || []).map((it, i) => (
                              <tr key={i}>
                                <td style={{ padding: 4 }} className="font-mono">{it.sku}</td>
                                <td style={{ padding: 4 }}>{it.name}</td>
                                <td style={{ padding: 4, textAlign: 'right' }}>{it.quantity}</td>
                                <td style={{ padding: 4, textAlign: 'right' }}>{inr(it.unit_price)}</td>
                                <td style={{ padding: 4, textAlign: 'right' }}>{inr(it.total_amount)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" data-testid="cn-modal">
          <div className="modal-content" style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>{editing ? `Edit ${editing.credit_note_number}` : 'Create Credit Note'}</h2></div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="grid grid-cols-2" style={{ gap: '12px' }}>
                  <div className="form-group">
                    <label className="form-label">Against Sales Order / Invoice</label>
                    <select className="form-select" value={formData.sales_order_id} onChange={(e) => onOrderChange(e.target.value)} data-testid="cn-order">
                      <option value="">None (manual)</option>
                      {orders.map(o => <option key={o.id} value={o.id}>{o.order_number} - {o.customer_name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Customer *</label>
                    <select className="form-select" value={formData.customer_id} onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })} required data-testid="cn-customer">
                      <option value="">Select customer...</option>
                      {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Original Invoice No.</label>
                    <input type="text" className="form-input" value={formData.original_invoice_number} onChange={(e) => setFormData({ ...formData, original_invoice_number: e.target.value })} placeholder="Invoice being replaced/cancelled" data-testid="cn-invoice" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Reason</label>
                    <input type="text" className="form-input" value={formData.reason} onChange={(e) => setFormData({ ...formData, reason: e.target.value })} placeholder="e.g. Invoice revised / cancelled" data-testid="cn-reason" />
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', margin: '16px 0 8px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: 700, margin: 0 }}>Items</h3>
                  <button type="button" onClick={addItem} className="btn btn-secondary btn-sm"><Plus size={12} /> Add Item</button>
                </div>
                <table style={{ width: '100%', fontSize: '12px' }}>
                  <thead><tr>
                    <th style={{ padding: 6 }}>SKU</th><th style={{ padding: 6 }}>Name</th>
                    <th style={{ padding: 6 }}>Qty</th><th style={{ padding: 6 }}>Rate</th><th style={{ padding: 6 }}>GST%</th>
                    <th style={{ padding: 6, textAlign: 'right' }}>Amount</th><th style={{ width: 32 }}></th>
                  </tr></thead>
                  <tbody>
                    {formData.items.map((item, idx) => {
                      const c = computeItem(item);
                      return (
                        <tr key={idx}>
                          <td style={{ padding: 4 }}>
                            <select className="form-select" value={item.sku} onChange={(e) => updateItem(idx, 'sku', e.target.value)} style={{ fontSize: 12, padding: 4 }}>
                              <option value="">Select...</option>
                              {inventory.map(i => <option key={i.sku} value={i.sku}>{i.sku}</option>)}
                            </select>
                          </td>
                          <td style={{ padding: 4 }}><input type="text" className="form-input" value={item.name} onChange={(e) => updateItem(idx, 'name', e.target.value)} required style={{ fontSize: 12, padding: 4, minWidth: 120 }} /></td>
                          <td style={{ padding: 4 }}><input type="number" className="form-input" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', e.target.value)} required style={{ fontSize: 12, padding: 4, width: 60 }} /></td>
                          <td style={{ padding: 4 }}><input type="number" step="0.01" className="form-input" value={item.unit_price} onChange={(e) => updateItem(idx, 'unit_price', e.target.value)} required style={{ fontSize: 12, padding: 4, width: 80 }} /></td>
                          <td style={{ padding: 4 }}><input type="number" className="form-input" value={item.gst_rate} onChange={(e) => updateItem(idx, 'gst_rate', e.target.value)} style={{ fontSize: 12, padding: 4, width: 50 }} /></td>
                          <td style={{ padding: 4, textAlign: 'right' }} className="tabular-nums">{inr(c.total_amount)}</td>
                          <td style={{ padding: 4 }}>{formData.items.length > 1 && <button type="button" onClick={() => removeItem(idx)} className="btn btn-ghost btn-sm"><X size={14} color="#DC2626" /></button>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div style={{ textAlign: 'right', marginTop: 8, fontWeight: 700 }}>Total: {inr(formTotal)}</div>

                <div className="form-group" style={{ marginTop: 12 }}>
                  <label className="form-label">Notes</label>
                  <textarea className="form-textarea" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => { setShowModal(false); setEditing(null); }} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-cn-btn">{editing ? 'Update Credit Note' : 'Create Credit Note'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditNotes;
